<template>
  <header class="public-nav sticky top-0 z-50 border-b border-base-300/80 bg-base-100/95 backdrop-blur-md dark:bg-base-200/95">
    <div class="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
      <router-link :to="routeManager.homePath()" class="tmi-logo shrink-0" aria-label="True Market Insiders home"></router-link>

      <nav class="hidden min-w-0 flex-1 items-center justify-center md:flex" aria-label="Main">
        <ul class="flex items-center gap-1">
          <li v-if="showProductsMenuItem">
            <details
              id="products-menu"
              class="dropdown dropdown-hover"
              @mouseenter="showMenuOnFocus"
              @mouseleave="hideMenuOnBlur"
            >
              <summary class="public-nav-link">{{ labelService.label( 'lblProducts' ) }}</summary>
              <ul class="menu dropdown-content w-72" @click="hideMenuOnBlur">
                <template v-if="showProductBrochures">
                  <li class="menu-title text-jade pl-2 pb-0 uppercase">{{ labelService.label( 'lblAvailableServices' ) }}</li>
                  <li v-for="product in productBrochures" :key="product.slug">
                    <router-link :to="routeManager.productRouteBySlug( product.slug )">
                      <img loading="lazy" v-if="product.favicon" :src="product.favicon" class="h-6 w-6 rounded-lg" alt="">
                      <span>{{ product.name }}</span>
                    </router-link>
                  </li>
                </template>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="routeManager.articleSearchPath()" class="public-nav-link">
              {{ labelService.label( 'lblArticles' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="routeManager.sectorEdgePath()" class="public-nav-link">
              {{ labelService.label( 'lblSectorEdge' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="contactUsPath" class="public-nav-link">
              {{ labelService.label( 'lblContact' ) }}
            </router-link>
          </li>
        </ul>
      </nav>

      <div class="ml-auto flex items-center gap-2 sm:gap-3">
        <router-link
          :to="routeManager.signInPath()"
          class="btn btn-primary btn-sm inline-flex min-h-9 shrink-0 px-4 text-sm font-semibold no-underline sm:min-h-10 sm:px-5"
        >
          {{ labelService.label( 'lblLogIn' ) }}
        </router-link>
        <DarkModeToggle />
        <button
          type="button"
          class="btn btn-ghost btn-square md:hidden"
          :aria-expanded="showMobileMenu"
          aria-controls="public-mobile-menu"
          @click="showMobileMenu = !showMobileMenu"
        >
          <span class="sr-only">Menu</span>
          <i class="pi text-lg" :class="showMobileMenu ? 'pi-times' : 'pi-bars'"></i>
        </button>
      </div>
    </div>

    <div
      v-if="showMobileMenu"
      id="public-mobile-menu"
      class="border-t border-base-300 bg-base-100 md:hidden dark:bg-base-200"
    >
      <nav class="mx-auto max-w-7xl px-4 py-4 sm:px-6" aria-label="Mobile">
        <ul class="menu menu-lg gap-1 p-0">
          <li v-if="showProductsMenuItem">
            <details>
              <summary class="font-semibold">{{ labelService.label( 'lblProducts' ) }}</summary>
              <ul>
                <template v-if="showProductBrochures">
                  <li v-for="product in productBrochures" :key="product.slug">
                    <router-link :to="routeManager.productRouteBySlug( product.slug )">
                      <img loading="lazy" v-if="product.favicon" :src="product.favicon" class="h-6 w-6 rounded-lg" alt="">
                      <span>{{ product.name }}</span>
                    </router-link>
                  </li>
                </template>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="routeManager.articleSearchPath()" class="font-semibold">
              {{ labelService.label( 'lblArticles' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="routeManager.sectorEdgePath()" class="font-semibold">
              {{ labelService.label( 'lblSectorEdge' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="contactUsPath" class="font-semibold">
              {{ labelService.label( 'lblContact' ) }}
            </router-link>
          </li>
        </ul>
        <router-link
          :to="routeManager.signInPath()"
          class="btn btn-primary btn-block mt-4 font-semibold no-underline"
        >
          {{ labelService.label( 'lblLogIn' ) }}
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { Router, useRouter } from "vue-router";
import routeManager from "@/router/route-manager";

import DarkModeToggle from '@/components/layout/DarkModeToggle.vue';

import type { Product } from "@tmi/types/entities";
import { useProductsStore } from "@/stores/Products";
const productsStore = useProductsStore();
const { availableProducts } = storeToRefs( productsStore );

import { useMenuPermissions } from "@/composables/menu-permissions";
const { showProductsMenuItem } = useMenuPermissions();

import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const router: Router = useRouter();

const contactUsPath = ref< string >( routeManager.contactUsPath() );

const hasAvailableProducts = computed< boolean >( () => availableProducts.value?.length > 0 );

const showProductBrochures = computed< boolean >( () => hasAvailableProducts.value );
const productBrochures = computed< Product[] >( () => {
  let products: Product[] = [];
  availableProducts.value.forEach( ( product: Product ) => products.push( product ) );
  return products;
} );

const showMobileMenu = ref< boolean >( false );
router.beforeEach( () => {
  showMobileMenu.value = false;
} );

function hideMenuOnBlur ( event: MouseEvent ) {
  if ( event.target ) {
    ( event.target as Element ).removeAttribute( "open" );
  }
}

function showMenuOnFocus ( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}
</script>

<style scoped>
a.tmi-logo {
  display: block;
  width: 11rem;
  height: 2.25rem;
  background-image: url("/img/logos/logo-green.svg");
  background-repeat: no-repeat;
  background-size: contain;
  background-position: left center;
}

[data-theme="dark"] a.tmi-logo {
  background-image: url("/img/logos/logo-white.svg");
}

.public-nav-link {
  display: inline-flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0 1rem;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.25;
  color: var(--color-base-content);
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.public-nav-link:hover,
.public-nav :deep(summary.public-nav-link:hover) {
  background-color: color-mix(in oklch, var(--color-base-content) 8%, transparent);
}

.public-nav-link.router-link-exact-active,
.public-nav :deep(.dropdown:has(ul a.router-link-active) summary.public-nav-link) {
  background-color: var(--color-base-200);
  font-weight: 600;
}

.public-nav :deep(summary.public-nav-link) {
  list-style: none;
  cursor: pointer;
}

.public-nav :deep(summary.public-nav-link::-webkit-details-marker) {
  display: none;
}
</style>
